import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, InputLabel, MenuItem, Paper, Select, Snackbar, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Radio, RadioGroup
} from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import api from '../api';
import { ROLLEN, Rolle } from '../konto';

interface Konto { id: number; email: string; name: string; rolle: Rolle; aktiv: boolean }
interface Formular { id: number | null; email: string; name: string; rolle: Rolle; aktiv: boolean; zugang: 'einladung' | 'passwort'; passwort: string }

const fehlerText = (e: any, standard: string) => e?.response?.data?.fehler || standard;
const rolleLabel = (r: Rolle) => ROLLEN.find(x => x.wert === r)?.label || r;
const LEER: Formular = { id: null, email: '', name: '', rolle: 'standard', aktiv: true, zugang: 'einladung', passwort: '' };

export default function Benutzer({ eigeneId }: { eigeneId?: number }) {
  const [liste, setListe] = useState<Konto[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [meldung, setMeldung] = useState('');
  const [formular, setFormular] = useState<Formular | null>(null);
  const [formFehler, setFormFehler] = useState('');
  const [arbeitet, setArbeitet] = useState(false);

  const laden = useCallback(() => {
    api.get('/auth/benutzer').then(r => setListe(r.data)).catch(e => setFehler(fehlerText(e, 'Benutzer konnten nicht geladen werden.')));
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const oeffnen = (k?: Konto) => {
    setFormFehler('');
    setFormular(k ? { ...LEER, ...k, zugang: 'einladung', passwort: '' } : { ...LEER });
  };

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formular) return;
    setArbeitet(true);
    setFormFehler('');
    const { id, email, name, rolle, aktiv, zugang, passwort } = formular;
    try {
      if (id === null) {
        const r = await api.post('/auth/benutzer', { email, name, rolle, ...(zugang === 'passwort' ? { passwort } : {}) });
        setMeldung(r.data.einladung ? `${name} angelegt – Einladung an ${email} gesendet` : `${name} angelegt`);
      } else {
        await api.put(`/auth/benutzer/${id}`, { email, name, rolle, aktiv });
        if (passwort) await api.post(`/auth/benutzer/${id}/passwort`, { passwort });
        setMeldung(`${name} gespeichert`);
      }
      setFormular(null);
      laden();
    } catch (err) {
      setFormFehler(fehlerText(err, 'Speichern fehlgeschlagen.'));
    } finally { setArbeitet(false); }
  };

  const linkSenden = async () => {
    if (!formular?.id) return;
    setArbeitet(true);
    setFormFehler('');
    try {
      await api.post(`/auth/benutzer/${formular.id}/passwort`, {});
      setMeldung(`Link zum Festlegen des Passworts an ${formular.email} gesendet`);
      setFormular(null);
    } catch (err) {
      setFormFehler(fehlerText(err, 'Der Link konnte nicht gesendet werden.'));
    } finally { setArbeitet(false); }
  };

  const setze = (teil: Partial<Formular>) => setFormular(f => (f ? { ...f, ...teil } : f));
  const eigenes = formular?.id === eigeneId;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Benutzer</Typography>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => oeffnen()}>Neuer Benutzer</Button>
      </Box>
      {fehler && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => { setFehler(''); laden(); }}>Erneut laden</Button>}>{fehler}</Alert>}
      {liste === null && !fehler && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
      {liste && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Name</TableCell><TableCell>E-Mail</TableCell><TableCell>Rolle</TableCell><TableCell>Status</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {liste.map(k => (
                <TableRow key={k.id} hover onClick={() => oeffnen(k)} sx={{ cursor: 'pointer', opacity: k.aktiv ? 1 : 0.55 }}>
                  <TableCell>{k.name}{k.id === eigeneId && <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}> (Sie)</Typography>}</TableCell>
                  <TableCell>{k.email}</TableCell>
                  <TableCell>{rolleLabel(k.rolle)}</TableCell>
                  <TableCell>{k.aktiv ? <Chip size="small" color="success" variant="outlined" label="aktiv" /> : <Chip size="small" label="gesperrt" />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
        {ROLLEN.map(r => `${r.label}: ${r.text}`).join(' · ')}
      </Typography>

      <Dialog open={!!formular} onClose={arbeitet ? undefined : () => setFormular(null)} maxWidth="xs" fullWidth>
        {formular && (
          <form onSubmit={speichern}>
            <DialogTitle>{formular.id === null ? 'Neuer Benutzer' : formular.name}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
              {formFehler && <Alert severity="error">{formFehler}</Alert>}
              <TextField label="Name" required value={formular.name} onChange={e => setze({ name: e.target.value })} inputProps={{ maxLength: 80 }} />
              <TextField label="E-Mail (Anmeldename)" type="email" required value={formular.email} onChange={e => setze({ email: e.target.value })} inputProps={{ maxLength: 120 }} />
              <FormControl disabled={eigenes}>
                <InputLabel id="rolle-label">Rolle</InputLabel>
                <Select labelId="rolle-label" label="Rolle" value={formular.rolle} onChange={e => setze({ rolle: e.target.value as Rolle })}>
                  {ROLLEN.map(r => <MenuItem key={r.wert} value={r.wert}>{r.label} – {r.text}</MenuItem>)}
                </Select>
              </FormControl>
              {formular.id === null ? (
                <>
                  <RadioGroup value={formular.zugang} onChange={e => setze({ zugang: e.target.value as Formular['zugang'] })}>
                    <FormControlLabel value="einladung" control={<Radio />} label="Einladung per E-Mail – legt das Passwort selbst fest" />
                    <FormControlLabel value="passwort" control={<Radio />} label="Passwort jetzt vergeben" />
                  </RadioGroup>
                  {formular.zugang === 'passwort' && (
                    <TextField label="Passwort (mind. 8 Zeichen)" type="text" required autoComplete="off" value={formular.passwort}
                      onChange={e => setze({ passwort: e.target.value })} inputProps={{ minLength: 8, maxLength: 200 }} />
                  )}
                </>
              ) : (
                <>
                  <FormControlLabel disabled={eigenes} control={<Switch checked={formular.aktiv} onChange={e => setze({ aktiv: e.target.checked })} />}
                    label={formular.aktiv ? 'Aktiv – kann sich anmelden' : 'Gesperrt – kann sich nicht anmelden'} />
                  <TextField label="Neues Passwort (optional)" type="text" autoComplete="off" value={formular.passwort}
                    helperText="Leer lassen, um das Passwort nicht zu ändern" onChange={e => setze({ passwort: e.target.value })}
                    inputProps={{ minLength: 8, maxLength: 200 }} />
                  {!eigenes && <Button onClick={linkSenden} disabled={arbeitet} sx={{ alignSelf: 'flex-start' }}>Link zum Passwort-Festlegen per E-Mail senden</Button>}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setFormular(null)} disabled={arbeitet}>Abbrechen</Button>
              <Button type="submit" variant="contained" disabled={arbeitet}>{formular.id === null ? 'Anlegen' : 'Speichern'}</Button>
            </DialogActions>
          </form>
        )}
      </Dialog>

      <Snackbar open={!!meldung} autoHideDuration={4000} onClose={() => setMeldung('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setMeldung('')}>{meldung}</Alert>
      </Snackbar>
    </Box>
  );
}
